import { Component, OnDestroy, OnInit } from '@angular/core';
import { AnalyticsRangeContext, ComparePeriod } from '@services/analytics-services/core';
import { AnalyticsTrendWidgetComponent } from '@enginuity/analytics/organisms/analytics-trend-widget/analytics-trend-widget.component';
import { AsyncPipe } from '@angular/common';
import { combineLatest, Subject, takeUntil, tap } from 'rxjs';
import { ShippingAnalyticsService } from '@services/analytics-services/shipping/shipping-analytics.service';
import { HighChartContainerComponent } from '@enginuity/analytics/organisms/high-chart-container/high-chart-container.component';
import { ChartOptions } from '@services/analytics-services/hight-chart-configurations/high-chart-config';
import { ShippingAnalyticsCharts } from '@services/analytics-services/shipping/shipping-analytics.charts';
import { NotifyService } from '@services/core-services/notify.service';
import { CustomBarChartContainerComponent } from '@enginuity/analytics/organisms/custom-bar-chart-container/custom-bar-chart-container.component';
import { STATUS_COLORS } from '@services/analytics-services/hight-chart-configurations';
import { DashboardShipmentStore } from './dashboard-shipment.store';
import { DashboardStore } from '../dashboard/dashboard.store';

@Component({
  selector: 'app-dashboard-shipment',
  imports: [
    AnalyticsTrendWidgetComponent,
    AsyncPipe,
    HighChartContainerComponent,
    CustomBarChartContainerComponent,
  ],
  templateUrl: './dashboard-shipment.component.html',
  styleUrl: './dashboard-shipment.component.scss',
  providers: [ShippingAnalyticsCharts, DashboardShipmentStore],
})
export class DashboardShipmentComponent implements OnDestroy, OnInit {
  protected shipmentAnalytics$ = this.dashboardShipmentStore.getShippingAnalytics();
  protected stateWiseShipmentPerformanceStats$ =
    this.dashboardShipmentStore.getStateWiseShipmentPerformanceStats();
  protected stateWiseShipmentPerformanceTabs$ =
    this.dashboardShipmentStore.getStateWiseShipmentPerformanceTabs();
  protected stateWiseShipmentPerformanceTabsActiveTab$ =
    this.dashboardShipmentStore.getStateWiseShipmentPerformanceTabsActiveTab();
  protected stateWiseShipmentPerformancePagination$ =
    this.dashboardShipmentStore.getStateWiseShipmentPerformancePagination();

  protected comparePeriod!: ComparePeriod;
  protected context: AnalyticsRangeContext | undefined;
  protected shippingSLAChartOptions: ChartOptions = {};
  protected shippingByCourierChart: { label: string; value: number }[] = [];
  protected averageDeliveryByCourierChart: { label: string; value: number }[] = [];
  protected CourierChartOptions: ChartOptions = {};
  protected AverageDeliveryOptions: ChartOptions = {};
  private destroy$ = new Subject<void>();
  protected StateData: any[] = [];
  protected formatTooltipValue = (metric: any): string => {
    if (!metric) return '';

    // Get the value and check if it's a time-based metric
    const isTimeMetric = ['avg_delivery_time', 'avg_time_to_ship', 'avg_transit_time'].includes(
      metric.key
    );

    const value = metric.value;

    if (isTimeMetric && typeof value === 'number') {
      return `${value.toFixed(1)} days`;
    }

    // For regular metrics (Shipped, Early, On time, Late)
    if (typeof value === 'number') {
      if (value >= 1000) {
        return `${(value / 1000).toFixed(1)}k`;
      }
      return `${value}`;
    }

    return '';
  };

  private formatMetricLabel(metric: string): string {
    return metric.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  // Title arrays for different chart sections
  protected stateWiseShipmentPerformanceTitles: string[] = [
    'Shipped',
    'Early',
    'On time',
    'Late',
    'Shipping time',
    'Transit time',
    'Delivery time',
  ];

  constructor(
    private readonly dashboardStore: DashboardStore,
    private readonly dashboardShipmentStore: DashboardShipmentStore,
    private readonly shippingAnalyticsService: ShippingAnalyticsService,
    private readonly shippingAnalyticsCharts: ShippingAnalyticsCharts,
    private notify: NotifyService
  ) {
    this.dashboardStore
      .getRangeContext()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: context => this.fetchAnalytics(context),
        error: err => {
          this.notify.error(err);
        },
      });
  }

  selectedMetric = 'avg_delivery_time'; // default metric

  ngOnInit() {
    combineLatest([
      this.stateWiseShipmentPerformanceStats$,
      this.stateWiseShipmentPerformanceTabsActiveTab$,
    ])
      .pipe(
        takeUntil(this.destroy$),
        tap(([, activeTab]) => {
          this.selectedMetric = this.mapTabIdToMetric(activeTab);
        })
      )
      .subscribe();
  }

  private fetchAnalytics(context: AnalyticsRangeContext): void {
    this.context = context;

    this.shippingAnalyticsService
      .getShippingSLAStats(context)
      .pipe(
        takeUntil(this.destroy$),
        tap(response => {
          const chartData = response.list.map(
            (item: { slo_status: string; shipment_count: number }) => ({
              label: item.slo_status,
              y: item.shipment_count,
              color: STATUS_COLORS[item.slo_status] || STATUS_COLORS['neutral'],
            })
          );

          this.shippingSLAChartOptions = {
            data: chartData,
            title: 'Delivery Status Distribution',
            tooltipFormatter: (point: Highcharts.Point) => `
              <div class="custom-chart-tooltip" style="padding:12px;">
                <div class="d-flex flex-column spacing-8 align-items-start w-100">
                  <h4 class="h4-bold primary-0 text-capitalize" style="margin: 0">${point.name}</h4>
                  <span class="dash-border w-100"></span>
                  <div class="d-flex align-items-center justify-content-between spacing-8 w-100">
                    <div class="d-flex align-items-center spacing-4 w-100 text-center">
                      <div style="background:${point.color}" class="tooltipDot d-flex align-items-center h4-bold">&nbsp;</div>
                      <div class="h4-reg primary-50">Shipments</div>
                    </div>
                    <div class="h4-bold primary-0">${point.y}</div>
                  </div>
                </div>
              </div>
            `,
          };
        })
      )
      .subscribe({ error: err => console.error('Error fetching shipping SLA stats:', err) });

    this.shippingAnalyticsCharts
      .getShippingByCourierCharts(this.context)
      .pipe(
        takeUntil(this.destroy$),
        tap((data: { label: string; value: number }[]) => {
          this.shippingByCourierChart = data;
          this.CourierChartOptions = {
            categories: this.shippingByCourierChart.map(item => item.label ?? 'null'),
            data: this.shippingByCourierChart.map(item => ({
              y: item.value,
              label: item.label,
            })),
            yAxisTitle: 'Number of Shipments',
            tooltipFormatter: (point: Highcharts.Point) => `
                <div class="custom-chart-tooltip" style="padding:12px;">
                  <div class="d-flex flex-column spacing-8 align-items-start w-100">
                    <h4 class="h4-bold primary-0 text-capitalize" style="margin: 0">${point.options.label}</h4>
                    <span class="dash-border w-100"></span>
                    <div class="d-flex align-items-center justify-content-between spacing-8 w-100">
                      <div class="d-flex align-items-center spacing-4 w-100 text-center">
                        <div style="background:${point.color}" class="tooltipDot d-flex align-items-center h4-bold"> </div>
                        <div class="h4-reg primary-50">Shipped Order</div>
                      </div>
                      <div class="h4-bold primary-0">${point.options.y}</div>
                    </div>
                  </div>
                </div>
              `,
          };
        })
      )
      .subscribe({ error: err => console.error('Error loading chart data:', err) });

    this.shippingAnalyticsCharts
      .getAverageDeliveryByCourierCharts(this.context)
      .pipe(
        takeUntil(this.destroy$),
        tap((data: { label: string; value: number }[]) => {
          this.averageDeliveryByCourierChart = data;

          this.AverageDeliveryOptions = {
            categories: this.averageDeliveryByCourierChart.map(item => item.label ?? 'null'),
            data: this.averageDeliveryByCourierChart.map(item => ({
              y: item.value,
              label: item.label,
            })),
            yAxisTitle: 'Number of Average',
            tooltipFormatter: (point: Highcharts.Point) => `
                <div class="custom-chart-tooltip" style="padding:12px;">
                  <div class="d-flex flex-column spacing-8 align-items-start w-100">
                    <h4 class="h4-bold primary-0 text-capitalize" style="margin: 0">${point.options.label}</h4>
                    <span class="dash-border w-100"></span>
                    <div class="d-flex align-items-center justify-content-between spacing-8 w-100">
                      <div class="d-flex align-items-center spacing-4 w-100 text-center">
                        <div style="background:${point.color}" class="tooltipDot d-flex align-items-center h4-bold"> </div>
                        <div class="h4-reg primary-50">Average Delivery Time</div>
                      </div>
                      <div class="h4-bold primary-0">${point.y} days</div>
                    </div>
                  </div>
                </div>
              `,
          };
        })
      )
      .subscribe({ error: err => console.error('Error loading chart data:', err) });
  }

  protected onStateWiseShipmentPerformanceTabChange(tabId: string): void {
    this.dashboardShipmentStore.updateStatePerformanceDefaultTab(tabId);
  }

  mapTabIdToMetric(tabId: string): string {
    const mapping: Record<string, string> = {
      delivery: 'avg_delivery_time',
      ship: 'avg_time_to_ship',
      transit: 'avg_transit_time',
      // add more as needed
    };
    return mapping[tabId] || 'avg_delivery_time';
  }

  protected onStateWiseShipmentPerformancePageChange(event: {
    tabId: string;
    page: number;
    limit: number;
  }): void {
    this.dashboardShipmentStore.updateStatePerformancePagination(
      event.tabId,
      event.page,
      event.limit
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
